import { useCallback, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Check, CircleDot, X, ChevronDown, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  getFollowupSuggestions,
  approveFollowupSuggestion,
  dismissFollowupSuggestion,
  type MeetingFollowupSuggestion,
} from '../api'
import { useSwipeToDismiss } from '@tendbot/ui'

interface MeetingFollowupSuggestionsProps {
  entryId: string
  /** Bump this value to force a refetch (e.g. after summary regenerate). */
  refreshKey?: string | number
}

type Pending =
  | { kind: 'approve'; suggestion: MeetingFollowupSuggestion }
  | null

function formatDueDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export default function MeetingFollowupSuggestions({
  entryId,
  refreshKey,
}: MeetingFollowupSuggestionsProps) {
  const { t } = useTranslation('recording')
  const [suggestions, setSuggestions] = useState<MeetingFollowupSuggestion[]>([])
  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState<Pending>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [showDismissed, setShowDismissed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getFollowupSuggestions(entryId)
      .then(rows => { if (!cancelled) setSuggestions(rows) })
      .catch(() => { if (!cancelled) setSuggestions([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [entryId, refreshKey])

  const handleApprove = useCallback(async (suggestion: MeetingFollowupSuggestion) => {
    setBusyId(suggestion.id)
    try {
      const updated = await approveFollowupSuggestion(entryId, suggestion.id)
      setSuggestions(prev => prev.map(s => s.id === updated.id ? updated : s))
    } catch {
      // Leave the row as-is; optional: surface a toast
    } finally {
      setBusyId(null)
      setPending(null)
    }
  }, [entryId])

  const handleDismiss = useCallback(async (suggestion: MeetingFollowupSuggestion) => {
    setBusyId(suggestion.id)
    try {
      const updated = await dismissFollowupSuggestion(entryId, suggestion.id)
      setSuggestions(prev => prev.map(s => s.id === updated.id ? updated : s))
    } catch {
      // swallow
    } finally {
      setBusyId(null)
    }
  }, [entryId])

  if (loading || suggestions.length === 0) return null

  const pendingItems = suggestions.filter(s => s.status === 'suggested')
  const approvedItems = suggestions.filter(s => s.status === 'approved')
  const dismissedItems = suggestions.filter(s => s.status === 'dismissed')

  if (pendingItems.length === 0 && approvedItems.length === 0 && dismissedItems.length === 0) {
    return null
  }

  return (
    <>
      <div className="rounded-xl border border-gold/10 bg-ivory/[0.02] p-4 space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-pearl/60">
          {t('suggestedFollowups.title')}
        </h3>

        {pendingItems.length > 0 && (
          <div className="space-y-2">
            {pendingItems.map(s => (
              <SuggestionRow
                key={s.id}
                suggestion={s}
                busy={busyId === s.id}
                onApprove={() => setPending({ kind: 'approve', suggestion: s })}
                onDismiss={() => handleDismiss(s)}
                t={t}
              />
            ))}
          </div>
        )}

        {approvedItems.length > 0 && (
          <div className="space-y-1.5 pt-1">
            {approvedItems.map(s => (
              <div
                key={s.id}
                className="flex items-center gap-2 text-xs text-pearl/50 px-2 py-1"
              >
                <Check size={12} className="text-emerald shrink-0" />
                <span className="truncate">{s.summary}</span>
                <span className="text-pearl/40 shrink-0">· {t('suggestedFollowups.added')}</span>
              </div>
            ))}
          </div>
        )}

        {dismissedItems.length > 0 && (
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setShowDismissed(v => !v)}
              className="flex items-center gap-1 text-[11px] text-pearl/40 hover:text-pearl/60 transition-colors"
            >
              {showDismissed ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              {t('suggestedFollowups.dismissedCount', { count: dismissedItems.length })}
            </button>
            <AnimatePresence>
              {showDismissed && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-1 mt-1.5 overflow-hidden"
                >
                  {dismissedItems.map(s => (
                    <div
                      key={s.id}
                      className="flex items-center gap-2 text-xs text-pearl/40 px-2 py-1"
                    >
                      <X size={12} className="shrink-0" />
                      <span className="truncate line-through">{s.summary}</span>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      <ConfirmFollowupSheet
        open={pending?.kind === 'approve'}
        suggestion={pending?.kind === 'approve' ? pending.suggestion : null}
        busy={pending?.kind === 'approve' && busyId === pending.suggestion.id}
        onConfirm={() => pending?.kind === 'approve' && handleApprove(pending.suggestion)}
        onClose={() => setPending(null)}
      />
    </>
  )
}

// ─── Row ────────────────────────────────────────────────────

interface SuggestionRowProps {
  suggestion: MeetingFollowupSuggestion
  busy: boolean
  onApprove: () => void
  onDismiss: () => void
  t: (key: string, opts?: Record<string, unknown>) => string
}

function SuggestionRow({ suggestion, busy, onApprove, onDismiss, t }: SuggestionRowProps) {
  const due = formatDueDate(suggestion.expectedBy)
  return (
    <div className="flex items-start gap-2 rounded-lg border border-sage/15 bg-sage/[0.04] p-2.5">
      <CircleDot size={14} className="mt-0.5 text-sage shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-pearl/90">{suggestion.summary}</p>
        {(suggestion.waitingOn || due) && (
          <div className="flex flex-wrap gap-2 mt-1 text-[11px] text-pearl/50">
            {suggestion.waitingOn && <span>· {suggestion.waitingOn}</span>}
            {due && <span>· {t('suggestedFollowups.dueBy', { date: due })}</span>}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={onDismiss}
          disabled={busy}
          className="px-2 py-1 rounded-md text-xs text-pearl/50 hover:text-pearl/80 hover:bg-white/5 transition-colors disabled:opacity-40"
          aria-label={t('suggestedFollowups.dismiss')}
        >
          <X size={14} />
        </button>
        <button
          type="button"
          onClick={onApprove}
          disabled={busy}
          className="px-3 py-1 rounded-md text-xs font-medium text-sage border border-sage/30 bg-sage/10 hover:bg-sage/20 transition-colors disabled:opacity-40"
        >
          {t('suggestedFollowups.approve')}
        </button>
      </div>
    </div>
  )
}

// ─── Confirm sheet ──────────────────────────────────────────

interface ConfirmFollowupSheetProps {
  open: boolean
  suggestion: MeetingFollowupSuggestion | null
  busy: boolean
  onConfirm: () => void
  onClose: () => void
}

function ConfirmFollowupSheet({ open, suggestion, busy, onConfirm, onClose }: ConfirmFollowupSheetProps) {
  const { t } = useTranslation('recording')
  const { sheetProps, backdropOpacity } = useSwipeToDismiss({ onClose })
  const due = suggestion ? formatDueDate(suggestion.expectedBy) : null

  return (
    <AnimatePresence>
      {open && suggestion && (
        <>
          <motion.div
            key="confirm-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{ opacity: backdropOpacity }}
            className="fixed inset-0 bg-black/50 z-50"
          />
          <motion.div
            key="confirm-sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            {...sheetProps}
            className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl bg-charcoal border-t border-white/10 backdrop-blur-xl pb-safe"
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>
            <div className="px-5 pb-5 space-y-4">
              <div>
                <h3 className="text-base font-medium text-ivory">{t('suggestedFollowups.confirmTitle')}</h3>
                <p className="text-xs text-pearl/60 mt-1">{t('suggestedFollowups.confirmBody')}</p>
              </div>

              <div className="rounded-lg border border-sage/20 bg-sage/[0.05] p-3 space-y-1">
                <p className="text-sm text-pearl/90">{suggestion.summary}</p>
                {(suggestion.waitingOn || due) && (
                  <div className="flex flex-wrap gap-2 text-[11px] text-pearl/50">
                    {suggestion.waitingOn && <span>{suggestion.waitingOn}</span>}
                    {due && <span>· {t('suggestedFollowups.dueBy', { date: due })}</span>}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="flex-1 py-2.5 rounded-lg text-sm text-pearl/80 bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-50"
                >
                  {t('suggestedFollowups.cancel')}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={busy}
                  className="flex-1 py-2.5 rounded-lg text-sm font-medium text-sage border border-sage/30 bg-sage/10 hover:bg-sage/20 transition-colors disabled:opacity-50"
                >
                  {busy ? t('suggestedFollowups.confirming') : t('suggestedFollowups.confirm')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
